/**
 * Alert Service
 * Handles creation and management of alerts for SPOC notifications
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

/**
 * Create an alert for hash not found
 * @param {Object} params - Alert parameters
 * @returns {Promise<Object>} Created alert
 */
export async function createHashNotFoundAlert({
  studentName,
  rollNumber,
  certificateHash,
  certificateCid,
  institutionId,
  institutionName,
  verificationAttemptBy,
  additionalDetails = {}
}) {
  if (!isSupabaseConfigured()) {
    console.warn('⚠️ Supabase not configured, cannot create alert');
    return null;
  }

  try {
    console.log('🔔 Creating hash not found alert...');
    
    const alert = {
      alert_type: 'hash_not_found',
      severity: 'warning',
      status: 'unread',
      student_name: studentName,
      roll_number: rollNumber,
      certificate_hash: certificateHash,
      certificate_cid: certificateCid,
      institution_id: institutionId,
      institution_name: institutionName,
      verification_attempt_by: verificationAttemptBy,
      title: 'Certificate Not Found in Database',
      message: `A verification attempt was made for ${studentName || 'a student'}${rollNumber ? ` (Roll: ${rollNumber})` : ''}, but the certificate was not found in the database. This may indicate an unregistered certificate or potential fraud.`,
      details: {
        hash: certificateHash,
        cid: certificateCid,
        attempted_by: verificationAttemptBy,
        timestamp: new Date().toISOString(),
        ...additionalDetails
      }
    };

    const { data, error } = await supabase
      .from('alerts')
      .insert(alert)
      .select()
      .single();

    if (error) {
      console.error('❌ Failed to create alert:', error);
      throw error;
    }

    console.log('✅ Alert created successfully:', data.id);
    return data;
  } catch (error) {
    console.error('❌ Error creating alert:', error);
    return null;
  }
}

/**
 * Get alerts for an institution
 * @param {string} institutionId - Institution ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} List of alerts
 */
export async function getAlertsForInstitution(institutionId, options = {}) {
  if (!isSupabaseConfigured()) {
    console.warn('⚠️ Supabase not configured');
    return [];
  }

  try {
    const {
      status = null, // 'unread', 'read', 'resolved', 'dismissed'
      severity = null, // 'info', 'warning', 'critical'
      limit = 50,
      offset = 0
    } = options;

    let query = supabase
      .from('alerts')
      .select('*')
      .eq('institution_id', institutionId)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    if (severity) {
      query = query.eq('severity', severity);
    }

    query = query.range(offset, offset + limit - 1);

    const { data, error } = await query;

    if (error) {
      console.error('❌ Failed to fetch alerts:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('❌ Error fetching alerts:', error);
    return [];
  }
}

/**
 * Get unread alert count for an institution
 * @param {string} institutionId - Institution ID
 * @returns {Promise<number>} Count of unread alerts
 */
export async function getUnreadAlertCount(institutionId) {
  if (!isSupabaseConfigured()) {
    return 0;
  }

  try {
    const { count, error } = await supabase
      .from('alerts')
      .select('*', { count: 'exact', head: true })
      .eq('institution_id', institutionId)
      .eq('status', 'unread');

    if (error) {
      console.error('❌ Failed to count alerts:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('❌ Error counting alerts:', error);
    return 0;
  }
}

/**
 * Mark alert as read
 * @param {string} alertId - Alert ID
 * @returns {Promise<Object>} Updated alert
 */
export async function markAlertAsRead(alertId) {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('alerts')
      .update({
        status: 'read',
        read_at: new Date().toISOString()
      })
      .eq('id', alertId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('❌ Error marking alert as read:', error);
    return null;
  }
}

/**
 * Mark alert as resolved
 * @param {string} alertId - Alert ID
 * @param {string} userId - User who resolved the alert
 * @returns {Promise<Object>} Updated alert
 */
export async function markAlertAsResolved(alertId, userId = null) {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('alerts')
      .update({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolved_by: userId
      })
      .eq('id', alertId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('❌ Error marking alert as resolved:', error);
    return null;
  }
}

/**
 * Dismiss an alert
 * @param {string} alertId - Alert ID
 * @returns {Promise<Object>} Updated alert
 */
export async function dismissAlert(alertId) {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('alerts')
      .update({
        status: 'dismissed'
      })
      .eq('id', alertId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('❌ Error dismissing alert:', error);
    return null;
  }
}

/**
 * Get alert statistics for an institution
 * @param {string} institutionId - Institution ID
 * @returns {Promise<Object>} Alert statistics
 */
export async function getAlertStatistics(institutionId) {
  if (!isSupabaseConfigured()) {
    return {
      total: 0,
      unread: 0,
      critical: 0,
      warning: 0,
      info: 0
    };
  }

  try {
    const { data, error } = await supabase
      .from('alerts')
      .select('status, severity')
      .eq('institution_id', institutionId);

    if (error) throw error;

    const stats = {
      total: data.length,
      unread: data.filter(a => a.status === 'unread').length,
      critical: data.filter(a => a.severity === 'critical').length,
      warning: data.filter(a => a.severity === 'warning').length,
      info: data.filter(a => a.severity === 'info').length
    };

    return stats;
  } catch (error) {
    console.error('❌ Error getting alert statistics:', error);
    return {
      total: 0,
      unread: 0,
      critical: 0,
      warning: 0,
      info: 0
    };
  }
}
